import { emailService } from '@/services/email.service';
import { trackingService } from '@/services/tracking.service';
import { userService } from '@/services/user.service';
import { courseService, type TermsAcceptanceRecord } from '@/services/course.service';
import type {
  ActivityLogItem,
  AttemptDetail,
  CourseInfo,
  CourseOverview,
  Lesson,
  Module,
  PaceCalculation,
  UserCourseData,
  UserCourseStats,
  UserInfo
} from '@/types/userCourseProgress.types';
import {
  Activity,
  AlertCircle,
  ArrowLeft,
  Bookmark,
  BookOpen,
  Briefcase,
  Building,
  CheckCircle,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ClipboardCheck,
  Clock,
  Eye,
  FileText,
  Headphones,
  Edit2,
  Lock,
  MessageSquare,
  Pause,
  Play,
  Save,
  Send,
  TrendingUp,
  Trophy,
  User,
  Video,
  XCircle,
  Zap
} from 'lucide-react';
import Modal from '@/components/ui/Modal';
import SimpleSelect from '@/components/ui/SimpleSelect';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';

import { useNavigate, useParams } from 'react-router-dom';

const filterOptions = [
  { label: 'All Status', value: 'all' },
  { label: 'Completed', value: 'completed' },
  { label: 'In Progress', value: 'in-progress' },
  { label: 'Not Started', value: 'not-started' },
];

export default function UserCourseProgress() {
  const { userId, courseId } = useParams();
  const navigate = useNavigate();
  const [expandedModules, setExpandedModules] = useState<string[]>([]);
  const [, setSelectedLesson] = useState<Lesson | null>(null);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showActivityLog] = useState(false);
  const [showDepartmentsModal, setShowDepartmentsModal] = useState(false);
  const [showRolesModal, setShowRolesModal] = useState(false);
  const [showAttemptsModal, setShowAttemptsModal] = useState(false);
  const [selectedAssessment, setSelectedAssessment] = useState<Lesson | null>(null);
  const [selectedAttempt, setSelectedAttempt] = useState<AttemptDetail | null>(null);
  const [emailContent, setEmailContent] = useState('');
  const [viewMode] = useState<'detailed' | 'compact'>('detailed');
  const [filterStatus, setFilterStatus] = useState<'all' | 'completed' | 'in-progress' | 'not-started'>('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showRevokeModal, setShowRevokeModal] = useState(false);
  const [revokeLoading, setRevokeLoading] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [showTitleModal, setShowTitleModal] = useState(false);
  const TITLE_CHAR_LIMIT = 30;
  const [rawTrackingData, setRawTrackingData] = useState<any>(null);
  const [editForm, setEditForm] = useState({
    is_mandatory: false,
    deadline_date: '',
    send_notification: false,
    enable_automatic_reminder: false,
    reminder_frequency_days: '' as any,
  });

  // Data states
  const [termsAcceptances, setTermsAcceptances] = useState<TermsAcceptanceRecord[]>([]);
  const [isTermsRequired, setIsTermsRequired] = useState(false);
  // Assessments the course's completion config requires a pass on.
  const [requiredAssessmentIds, setRequiredAssessmentIds] = useState<Set<string>>(new Set());
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [courseInfo, setCourseInfo] = useState<CourseInfo | null>(null);
  const [, setUserCourseStats] = useState<UserCourseStats | null>(null);
  const [userCourseData, setUserCourseData] = useState<UserCourseData | null>(null);
  const [activityLog] = useState<ActivityLogItem[]>([]);
  const [modules, setModules] = useState<Module[]>([]);
  const [courseOverview, setCourseOverview] = useState<CourseOverview | null>(null);

  // Get current user from localStorage or context
  const getCurrentUserId = () => {
    const userData = localStorage.getItem('userData');
    if (userData) {
      const parsed = JSON.parse(userData);
      return parsed.user_id || parsed._id || 'admin_001';
    }
    return 'admin_001';
  };

 // Handle dynamic back navigation
  const handleBackNavigation = () => {
    navigate(-1);
  };

  const handleRevokeCourse = async () => {
    if (!courseId || !userId) return;
    setRevokeLoading(true);
    try {
      const result = await trackingService.revokeCourseForUser(courseId, userId);
      toast.success(
        `Course revoked. Deleted: ${result.course_trackings_deleted} tracking(s), ${result.assessment_attempts_deleted} attempt(s), ${result.certificates_deleted} certificate(s).`
      );
      setShowRevokeModal(false);
      navigate(-1);
    } catch (err: any) {
      const message = err?.response?.data?.error?.error_description || err?.response?.data?.description || err?.message || 'Failed to revoke course';
      toast.error(message);
    } finally {
      setRevokeLoading(false);
    }
  };

  const openEditModal = () => {
    if (!rawTrackingData) return;
    const deadlineDate = rawTrackingData.deadline_date
      ? new Date(rawTrackingData.deadline_date).toISOString().split('T')[0]
      : '';
    setEditForm({
      is_mandatory: rawTrackingData.is_mandatory || false,
      deadline_date: deadlineDate,
      send_notification: rawTrackingData.send_notification ?? false,
      enable_automatic_reminder: rawTrackingData.enable_automatic_reminder ?? false,
      reminder_frequency_days: rawTrackingData.reminder_frequency_days || '',
    });
    setShowEditModal(true);
  };

  const handleSaveSettings = async () => {
    if (!rawTrackingData?._id) return;

    // Validate reminder_frequency_days
    if (editForm.enable_automatic_reminder) {
      const freq = Number(editForm.reminder_frequency_days);
      if (!freq || freq <= 0) {
        toast.error('Reminder frequency must be greater than 0');
        return;
      }
      if (freq > 100) {
        toast.error('Reminder frequency cannot exceed 100 days');
        return;
      }
    }

    // Validate deadline_date >= today — only when the admin actually changed the deadline.
    // If they're editing other fields (mandatory / reminders) on a course whose deadline
    // has already passed, we skip this check; the date picker blocks past dates for new selections anyway.
    if (editForm.deadline_date) {
      const originalDeadline = rawTrackingData?.deadline_date
        ? new Date(rawTrackingData.deadline_date).toISOString().split('T')[0]
        : '';
      const deadlineChanged = editForm.deadline_date !== originalDeadline;

      if (deadlineChanged) {
        const deadline = new Date(editForm.deadline_date);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (deadline < today) {
          toast.error('Deadline must be today or a future date');
          return;
        }
      }
    }

    const saved_send_notification = editForm.send_notification;
    const saved_enable_automatic_reminder = saved_send_notification ? editForm.enable_automatic_reminder : false;
    const saved_reminder_frequency_days = saved_send_notification && saved_enable_automatic_reminder && editForm.reminder_frequency_days ? Number(editForm.reminder_frequency_days) : null;
    const saved_deadline_iso = editForm.deadline_date ? new Date(editForm.deadline_date).toISOString() : null;

    setEditLoading(true);
    try {
      await trackingService.updateTrackingSettings(rawTrackingData._id, {
        is_mandatory: editForm.is_mandatory,
        deadline_date: editForm.deadline_date || undefined,
        send_notification: saved_send_notification,
        enable_automatic_reminder: saved_enable_automatic_reminder,
        reminder_frequency_days: saved_reminder_frequency_days,
      });

      // Optimistic local updates so the UI reflects changes immediately
      setRawTrackingData((prev: any) => prev ? ({
        ...prev,
        is_mandatory: editForm.is_mandatory,
        deadline_date: saved_deadline_iso,
        send_notification: saved_send_notification,
        enable_automatic_reminder: saved_enable_automatic_reminder,
        reminder_frequency_days: saved_reminder_frequency_days,
      }) : prev);
      setCourseInfo((prev: any) => prev ? ({
        ...prev,
        is_mandatory: editForm.is_mandatory,
        deadline: saved_deadline_iso ? formatDate(saved_deadline_iso) : undefined,
        deadlineRaw: saved_deadline_iso || undefined,
      }) : prev);

      toast.success('Tracking settings updated successfully');
      setShowEditModal(false);
      loadAllData();
    } catch (err: any) {
      const message = err?.response?.data?.error?.error_description || err?.message || 'Failed to update settings';
      toast.error(message);
    } finally {
      setEditLoading(false);
    }
  };

  // Helper function to format seconds into readable time
  const formatTime = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);

    const hh = hours.toString().padStart(2, '0');
    const mm = minutes.toString().padStart(2, '0');
    const ss = secs.toString().padStart(2, '0');

    return `${hh}:${mm}:${ss}`;
  };

  // Helper function to format date as dd/mm/yy
  const formatDate = (date: string | Date | undefined): string => {
    if (!date) return 'N/A';
    const d = new Date(date);
    const day = d.getDate().toString().padStart(2, '0');
    const month = (d.getMonth() + 1).toString().padStart(2, '0');
    const year = d.getFullYear().toString().slice(-2);
    return `${day}/${month}/${year}`;
  };

  // Load all data on mount
  useEffect(() => {
    if (userId && courseId) {
      loadAllData();
    }
  }, [userId, courseId]);

  // Terms & Conditions acceptance records for this learner (non-fatal — absent
  // when the course has no T&C condition configured).
  useEffect(() => {
    if (!userId || !courseId) return;
    let cancelled = false;

    Promise.all([
      courseService.getCompletionConfig(courseId).catch(() => null),
      courseService.getUserTermsAcceptances(courseId, userId).catch(() => null),
      courseService.listCourseAssessments(courseId).catch(() => [])
    ]).then(([config, acceptancesRes, assessments]) => {
      if (cancelled) return;

      setRequiredAssessmentIds(new Set(
        assessments.filter(a => a.required_for_completion).map(a => a.assessment_id)
      ));

      const hasActiveTerms = !!config?.custom_enabled && 
        (config.conditions || []).some(
          c => c.type === 'terms_and_conditions' && c.is_enabled
        );
      
      setIsTermsRequired(hasActiveTerms);
      setTermsAcceptances(acceptancesRes?.records || []);
    }).catch(() => {
      if (!cancelled) {
        setIsTermsRequired(false);
        setTermsAcceptances([]);
      }
    });

    return () => { cancelled = true; };
  }, [userId, courseId]);

  // Same "Mandatory" label as the learner sidebar (CourseNavigation).
  const renderMandatoryLabel = (lesson: Lesson) =>
    lesson.type === 'assessment' && requiredAssessmentIds.has(lesson.id) ? (
      <span
        className="flex-shrink-0 inline-block px-1.5 py-px rounded-full border border-red-200 bg-red-50 text-[10px] font-semibold leading-4 text-red-700"
        title="Required to complete the course"
      >
        Mandatory
      </span>
    ) : null;

  const loadAllData = async () => {
    setLoading(true);
    setError(null);
    try {
      // First get tracking data which contains course info
      // POST with course_id in body
      const trackingResponse = await trackingService.getUserProgress(userId!, { course_id: courseId! });
      
      // Handle the API response structure
      let courseTrackingData;
      if (trackingResponse && 'data' in trackingResponse && Array.isArray(trackingResponse.data)) {
        // Paginated response - find the specific course or use first item
        courseTrackingData = trackingResponse.data.find(course => course.course_id === courseId) || trackingResponse.data[0];
        if (!courseTrackingData) {
          throw new Error('Course not found in user\'s enrolled courses');
        }
      } else if (Array.isArray(trackingResponse)) {
        // If it's an array, find the specific course
        courseTrackingData = trackingResponse.find(course => course.course_id === courseId);
        if (!courseTrackingData) {
          throw new Error('Course not found in user\'s enrolled courses');
        }
      } else if (trackingResponse && typeof trackingResponse === 'object') {
        // If it's a single object, use it directly (API returns single object when courseId is provided)
        courseTrackingData = trackingResponse;
      } else {
        throw new Error('Invalid response format from tracking API');
      }

      // Store raw tracking data for edit modal
      setRawTrackingData(courseTrackingData);

      // Debug: Check if we have lesson_progress in the API response
      if (courseTrackingData.module_progress && courseTrackingData.module_progress.length > 0) {
        if (courseTrackingData.module_progress[0].lesson_progress) {
        } else {
        }
      }

      // Load user details using the working API
      let userDetails = null;
      try {
        const userResponse = await userService.getUserById(userId!);
        userDetails = userResponse?.data;
      } catch (error) {
        console.warn('Failed to load user details:', error);
      }

      // Process real user details
      if (userDetails) {
        const fullName = `${userDetails.given_name || ''} ${userDetails.family_name || ''}`.trim();

        // Handle departments - support both array and single string
        const departments = userDetails.departments || (userDetails.department ? [userDetails.department] : []);
        // Handle roles - support both array and single string
        const roles = userDetails.roles || (userDetails.role ? [userDetails.role] : []);

        setUserInfo({
          id: userDetails._id || userId!,
          name: fullName || userDetails.email || 'Unknown User',
          email: userDetails.email || 'No email',
          avatar: userDetails.profile_image,
          department: userDetails.department,
          departments: departments,
          role: userDetails.role,
          roles: roles,
          joinedDate: userDetails.joinedDate ? formatDate(userDetails.joinedDate) : undefined,
          lastActive: userDetails.lastActive ? formatDate(userDetails.lastActive) : undefined,
          totalCoursesEnrolled: userDetails?.totalCoursesEnrolled || 0,
          completedCourses: userDetails?.completedCourses || 0,
          inProgressCourses: userDetails.courses?.in_progress || 0,
          notStartedCourses: userDetails.courses?.not_started || 0,
          averageCompletionRate: userDetails.courses?.completion_rate || 0,
          learningStreak: userDetails?.learningStreak||0, // TODO: This field might need to be added to API
          metadata: userDetails
        });
      } else {
        // Fallback with basic info if API fails
        setUserInfo({
          id: userId!,
          name: 'User',
          email: 'Loading...',
          department: 'Unknown',
          role: 'User',
          joinedDate: undefined,
          lastActive: undefined,
          totalCoursesEnrolled: 0,
          completedCourses: 0
        });
      }

      // Process course details from tracking data
      setCourseInfo({
        id: courseTrackingData.course_id,
        title: courseTrackingData.course_title || 'Course Title',
        description: 'Course description',
        instructor: 'Course Instructor',
        category: courseTrackingData.category_name,
        difficulty: courseTrackingData.difficulty,
       duration: formatTime( (courseTrackingData.estimated_duration.total.hours * 3600) + (courseTrackingData.estimated_duration.total.minutes * 60)),
        totalModules: courseTrackingData.total_modules,
        totalLessons: courseTrackingData.module_progress?.reduce((sum: number, mod: any) => sum + mod.total_scenes, 0) || 0,
        passingScore: 80,
        certificate: courseTrackingData.certificate_issued,
        deadline: courseTrackingData.deadline_date ? formatDate(courseTrackingData.deadline_date) : undefined,
        deadlineRaw: courseTrackingData.deadline_date || undefined,
        is_mandatory: courseTrackingData.is_mandatory || false,
      });

      // Process modules from tracking data
      const processedModules = processModulesFromTracking(courseTrackingData);
      setModules(processedModules);
      
      // Calculate overall stats from real data
      const overallProgress = courseTrackingData.progress_percentage;
      const completedLessons = processedModules.reduce((acc, mod) => 
        acc + mod.lessons.filter(l => l.isCompleted).length, 0
      );
      const totalLessons = processedModules.reduce((acc, mod) => 
        acc + mod.lessons.length, 0
      );
      
      setUserCourseData({
        userId: userId!,
        courseId: courseId!,
        overallProgress,
        completedLessons,
        totalLessons,
        currentModule: getCurrentModuleFromTracking(courseTrackingData),
        currentLesson: getCurrentLessonFromTracking(courseTrackingData),
        status: courseTrackingData.status as any,
        grade: 82, // TODO: Add grade to API if available
        modules: processedModules
      });

      // Set course stats from real data
      setUserCourseStats({
        overallProgress: courseTrackingData.progress_percentage,
        timeInvested: courseTrackingData.total_time_spent_seconds,
        averageSessionTime: Math.round(courseTrackingData.total_time_spent_seconds / Math.max(courseTrackingData.attempts, 1)),
        totalTimeSpent: formatTime(courseTrackingData.total_time_spent_seconds),
        currentStreak: userDetails?.learningStreak?.current||0, // TODO: Calculate from activity log
        longestStreak: userDetails?.learningStreak?.longest ||0, // TODO: Calculate from activity log
        assignmentScore: 0, // TODO: Add to API
        quizScore: 0, // TODO: Add to API
        rank: 0, // TODO: Add to API
        totalStudents: 0, // TODO: Add to API
        predictedCompletion: courseTrackingData.deadline_date || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        recommendedPace: calculateRecommendedPace(courseTrackingData)
      });

      // Set initial expanded modules (all closed by default)
      setExpandedModules([]);

      // Load course overview metrics
      try {
        const overviewData = await trackingService.getUserCourseOverview(userId!, courseId!);
        setCourseOverview(overviewData);
      } catch (overviewError) {
        console.warn('Failed to load course overview:', overviewError);
      }

    } catch (err) {
      console.error('Error loading data:', err);
      setError('Failed to load course progress data');
      toast.error('Failed to load course progress data');
    } finally {
      setLoading(false);
    }
  };



  // Enhanced helper functions for processing new API tracking data
   const processModulesFromTracking = (trackingData: any): Module[] => {
  if (!trackingData || !trackingData.modules) return [];

  const allModules: Module[] = [];

  // 1. Add Course Pre-Assessments as a special "module"
  if (trackingData.course_pre_assessments && trackingData.course_pre_assessments.length > 0) {
    const coursePreLessons: Lesson[] = trackingData.course_pre_assessments.map((assessment: any, i: number) => ({
      id: assessment.assessment_id,
      moduleId: 'course-pre',
      title: assessment.title,
      description: 'Course Pre-Assessment',
      type: 'assessment',
      order: i + 1,
      duration: formatTime(assessment.time_spent_seconds || 0),
      durationMinutes: Math.round((assessment.time_spent_seconds || 0) / 60),
      isCompleted: assessment.status === 'COMPLETED',
      isLocked: false,
      progress: assessment.best_percentage || 0,
      timeSpent: formatTime(assessment.time_spent_seconds || 0),
      startedAt: assessment.started_at,
      completedAt: assessment.completed_at,
      lastAccessed: assessment.last_attempted_at,
      attempts: assessment.total_attempts,
      score: assessment.best_score,
      quiz: {
        totalQuestions: 0,
        score: assessment.best_percentage,
        passPercentage: assessment.passing_score,
        attempts: assessment.total_attempts,
        bestScore: assessment.best_score,
        passed: assessment.passed
      },
      attempts_detail: assessment.attempts_detail || []
    }));

    allModules.push({
      id: 'course-pre',
      title: '📋 Course Pre-Assessment',
      description: 'Complete this assessment before starting the course',
      order: 0,
      duration: formatTime(coursePreLessons.reduce((sum, l) => sum + (l.durationMinutes * 60), 0)),
      durationMinutes: coursePreLessons.reduce((sum, l) => sum + l.durationMinutes, 0),
      lessons: coursePreLessons,
      progress: coursePreLessons.length > 0 ? Math.round((coursePreLessons.filter(l => l.isCompleted).length / coursePreLessons.length) * 100) : 0,
      isLocked: false,
      timeSpent: coursePreLessons.reduce((sum, l) => sum + (l.durationMinutes * 60), 0),
      progress_percentage: coursePreLessons.length > 0 ? Math.round((coursePreLessons.filter(l => l.isCompleted).length / coursePreLessons.length) * 100) : 0
    });
  }

  // 2. Process regular modules
  trackingData.modules.forEach((moduleData: any, index: number) => {
    const lessons: Lesson[] = [];
    let lessonOrder = 0;

    // 2a. Add Module Pre-Assessments FIRST
    if (moduleData.pre_module_assessments && moduleData.pre_module_assessments.length > 0) {
      moduleData.pre_module_assessments.forEach((assessment: any) => {
        lessons.push({
          id: assessment.assessment_id,
          moduleId: moduleData.module_id,
          title: assessment.title,
          description: 'Module Pre-Assessment',
          type: 'assessment',
          order: lessonOrder++,
          duration: formatTime(assessment.time_spent_seconds || 0),
          durationMinutes: Math.round((assessment.time_spent_seconds || 0) / 60),
          isCompleted: assessment.status === 'COMPLETED',
          isLocked: false,
          progress: assessment.best_percentage || 0,
          timeSpent: formatTime(assessment.time_spent_seconds || 0),
          startedAt: assessment.started_at,
          completedAt: assessment.completed_at,
          lastAccessed: assessment.last_attempted_at,
          attempts: assessment.total_attempts,
          score: assessment.best_score,
          quiz: {
            totalQuestions: 0,
            score: assessment.best_percentage,
            passPercentage: assessment.passing_score,
            attempts: assessment.total_attempts,
            bestScore: assessment.best_score,
            passed: assessment.passed
          },
          attempts_detail: assessment.attempts_detail || [],
          indentLevel: 'module' // Module-level assessment
        } as any);
      });
    }

    // 2b. Process content_items with their pre/post assessments
    if (moduleData.content_items && moduleData.content_items.length > 0) {
      // Sort content items by their order
      const sortedContentItems = [...moduleData.content_items].sort((a, b) => (a.order || 0) - (b.order || 0));

      sortedContentItems.forEach((contentItem: any) => {
        // Find pre and post assessments for this content item
        const preAssessments = contentItem.assessments?.filter((a: any) => a.assessment_type === 'PRE') || [];
        const postAssessments = contentItem.assessments?.filter((a: any) => a.assessment_type === 'POST') || [];

        // Add Pre-Assessments for this content item
        preAssessments.forEach((assessment: any) => {
          lessons.push({
            id: assessment.assessment_id,
            moduleId: moduleData.module_id,
            title: assessment.title,
            description: `Pre-Assessment for ${contentItem.title}`,
            type: 'assessment',
            order: lessonOrder++,
            duration: formatTime(assessment.time_spent_seconds || 0),
            durationMinutes: Math.round((assessment.time_spent_seconds || 0) / 60),
            isCompleted: assessment.status === 'COMPLETED',
            isLocked: false,
            progress: assessment.best_percentage || 0,
            timeSpent: formatTime(assessment.time_spent_seconds || 0),
            startedAt: assessment.started_at,
            completedAt: assessment.completed_at,
            lastAccessed: assessment.last_attempted_at,
            attempts: assessment.total_attempts,
            score: assessment.best_score,
            quiz: {
              totalQuestions: 0,
              score: assessment.best_percentage,
              passPercentage: assessment.passing_score,
              attempts: assessment.total_attempts,
              bestScore: assessment.best_score,
              passed: assessment.passed
            },
            attempts_detail: assessment.attempts_detail || [],
            indentLevel: 'content' // Content-level assessment
          } as any);
        });

        // Add the actual content item
        lessons.push({
          id: contentItem.content_item_id,
          moduleId: moduleData.module_id,
          title: contentItem.title,
          description: contentItem.title,
          type: contentItem.type,
          order: lessonOrder++,
          duration: formatTime(contentItem.time_spent_seconds || 0),
          durationMinutes: Math.round((contentItem.time_spent_seconds || 0) / 60),
          isCompleted: contentItem.status === 'COMPLETED',
          isLocked: false,
          progress: contentItem.status === 'COMPLETED' ? 100 : 0,
          timeSpent: formatTime(contentItem.time_spent_seconds || 0),
          startedAt: contentItem.started_at,
          completedAt: contentItem.completed_at,
          lastAccessed: contentItem.last_accessed_at,
          indentLevel: 'content' // Content item
        } as any);

        // Add Post-Assessments for this content item
        postAssessments.forEach((assessment: any) => {
          lessons.push({
            id: assessment.assessment_id,
            moduleId: moduleData.module_id,
            title: assessment.title,
            description: `Post-Assessment for ${contentItem.title}`,
            type: 'assessment',
            order: lessonOrder++,
            duration: formatTime(assessment.time_spent_seconds || 0),
            durationMinutes: Math.round((assessment.time_spent_seconds || 0) / 60),
            isCompleted: assessment.status === 'COMPLETED',
            isLocked: false,
            progress: assessment.best_percentage || 0,
            timeSpent: formatTime(assessment.time_spent_seconds || 0),
            startedAt: assessment.started_at,
            completedAt: assessment.completed_at,
            lastAccessed: assessment.last_attempted_at,
            attempts: assessment.total_attempts,
            score: assessment.best_score,
            quiz: {
              totalQuestions: 0,
              score: assessment.best_percentage,
              passPercentage: assessment.passing_score,
              attempts: assessment.total_attempts,
              bestScore: assessment.best_score,
              passed: assessment.passed
            },
            attempts_detail: assessment.attempts_detail || [],
            indentLevel: 'content' // Content-level assessment
          } as any);
        });
      });
    }

    // 2c. Add Module Post-Assessments LAST
    if (moduleData.post_module_assessments && moduleData.post_module_assessments.length > 0) {
      moduleData.post_module_assessments.forEach((assessment: any) => {
        lessons.push({
          id: assessment.assessment_id,
          moduleId: moduleData.module_id,
          title: assessment.title,
          description: 'Module Post-Assessment',
          type: 'assessment',
          order: lessonOrder++,
          duration: formatTime(assessment.time_spent_seconds || 0),
          durationMinutes: Math.round((assessment.time_spent_seconds || 0) / 60),
          isCompleted: assessment.status === 'COMPLETED',
          isLocked: false,
          progress: assessment.best_percentage || 0,
          timeSpent: formatTime(assessment.time_spent_seconds || 0),
          startedAt: assessment.started_at,
          completedAt: assessment.completed_at,
          lastAccessed: assessment.last_attempted_at,
          attempts: assessment.total_attempts,
          score: assessment.best_score,
          quiz: {
            totalQuestions: 0,
            score: assessment.best_percentage,
            passPercentage: assessment.passing_score,
            attempts: assessment.total_attempts,
            bestScore: assessment.best_score,
            passed: assessment.passed
          },
          attempts_detail: assessment.attempts_detail || [],
          indentLevel: 'module' // Module-level assessment
        } as any);
      });
    }

    // Module progress calculation from lessons
    const completedLessons = lessons.filter(l => l.isCompleted).length;
    const calculatedProgress = lessons.length > 0 ? Math.round((completedLessons / lessons.length) * 100) : 0;

    allModules.push({
      id: moduleData.module_id,
      title: moduleData.title,
      description: moduleData.description || moduleData.title,
      order: moduleData.order ?? (index + 1),
      duration: formatTime(moduleData.time_spent_seconds || 0),
      durationMinutes: Math.round((moduleData.time_spent_seconds || 0) / 60),
      lessons,
      progress: moduleData.progress_percentage ?? calculatedProgress,
      isLocked: false,
      timeSpent: moduleData.time_spent_seconds,
      startedAt: moduleData.started_at,
      completedAt: moduleData.completed_at,
      progress_percentage: moduleData.progress_percentage ?? calculatedProgress
    });
  });

  // 3. Add Course Post-Assessments as a special "module"
  if (trackingData.course_post_assessments && trackingData.course_post_assessments.length > 0) {
    const coursePostLessons: Lesson[] = trackingData.course_post_assessments.map((assessment: any, i: number) => ({
      id: assessment.assessment_id,
      moduleId: 'course-post',
      title: assessment.title,
      description: 'Course Post-Assessment',
      type: 'assessment',
      order: i + 1,
      duration: formatTime(assessment.time_spent_seconds || 0),
      durationMinutes: Math.round((assessment.time_spent_seconds || 0) / 60),
      isCompleted: assessment.status === 'COMPLETED',
      isLocked: false,
      progress: assessment.best_percentage || 0,
      timeSpent: formatTime(assessment.time_spent_seconds || 0),
      startedAt: assessment.started_at,
      completedAt: assessment.completed_at,
      lastAccessed: assessment.last_attempted_at,
      attempts: assessment.total_attempts,
      score: assessment.best_score,
      quiz: {
        totalQuestions: 0,
        score: assessment.best_percentage,
        passPercentage: assessment.passing_score,
        attempts: assessment.total_attempts,
        bestScore: assessment.best_score,
        passed: assessment.passed
      },
      attempts_detail: assessment.attempts_detail || []
    }));

    allModules.push({
      id: 'course-post',
      title: '🎯 Course Post-Assessment',
      description: 'Final assessment to complete the course',
      order: 9999,
      duration: formatTime(coursePostLessons.reduce((sum, l) => sum + (l.durationMinutes * 60), 0)),
      durationMinutes: coursePostLessons.reduce((sum, l) => sum + l.durationMinutes, 0),
      lessons: coursePostLessons,
      progress: coursePostLessons.length > 0 ? Math.round((coursePostLessons.filter(l => l.isCompleted).length / coursePostLessons.length) * 100) : 0,
      isLocked: false,
      // timeSpent: coursePostLessons.reduce((sum, l) => sum + (l.durationMinutes * 60), 0),
      progress_percentage: coursePostLessons.length > 0 ? Math.round((coursePostLessons.filter(l => l.isCompleted).length / coursePostLessons.length) * 100) : 0
    });
  }

  // 4. Add Certificate section if issued
  if (trackingData.certificate_issued && trackingData.certificate_id) {
    allModules.push({
      id: 'certificate',
      title: '🏆 Certificate of Completion',
      description: 'Your course completion certificate',
      order: 10000,
      duration: '0m',
      durationMinutes: 0,
      lessons: [{
        id: trackingData.certificate_id,
        moduleId: 'certificate',
        title: 'Download Certificate',
        description: 'Certificate earned on completion',
        type: 'document',
        order: 1,
        duration: '0m',
        durationMinutes: 0,
        isCompleted: true,
        isLocked: false,
        progress: 100,
        timeSpent: '0m'
      }],
      progress: 100,
      isLocked: false,
      timeSpent: 0,
      progress_percentage: 100,
      completedAt: trackingData.completed_at
    });
  }

  return allModules.sort((a, b) => a.order - b.order);
 }

  const getCurrentModuleFromTracking = (trackingData: any): number => {
    if (!trackingData.module_progress) return 1;
    
    const inProgressIndex = trackingData.module_progress.findIndex((mod: any) => 
      mod.status === 'IN_PROGRESS'
    );
    
    return inProgressIndex >= 0 ? inProgressIndex + 1 : 1;
  };

  const getCurrentLessonFromTracking = (_trackingData: any): number => {
    // This would need more detailed lesson tracking data from the API
    return 1;
  };

  const calculateRecommendedPace = (trackingData: any): string => {
    if (!trackingData.deadline_date) return '1 lesson/day';
    
    const now = new Date();
    const deadline = new Date(trackingData.deadline_date);
    const daysRemaining = Math.max(1, Math.ceil((deadline.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)));
    
    const remainingLessons = trackingData.module_progress?.reduce((sum: number, mod: any) => 
      sum + (mod.total_scenes - (mod.completed_scenes?.length || 0)), 0
    ) || 0;
    
    const lessonsPerDay = Math.ceil(remainingLessons / daysRemaining);
    return `${lessonsPerDay} lesson${lessonsPerDay > 1 ? 's' : ''}/day`;
  };

  const toggleModule = (moduleId: string) => {
    setExpandedModules(prev =>
      prev.includes(moduleId)
        ? prev.filter(id => id !== moduleId)
        : [...prev, moduleId]
    );
  };

  const getLessonIcon = (type: string) => {
    switch (type) {
      case 'video': return <Video className="w-4 h-4 text-blue-600" />;
      case 'audio': return <Headphones className="w-4 h-4 text-purple-600" />;
      case 'document':
      case 'html':
      case 'text':
        return <FileText className="w-4 h-4 text-gray-600" />;
      case 'quiz':
      case 'assessment':
        return <ClipboardCheck className="w-4 h-4 text-orange-600" />;
      case 'assignment': return <BookOpen className="w-4 h-4 text-green-600" />;
      default: return <FileText className="w-4 h-4 text-gray-600" />;
    }
  };

  const getProgressColor = (progprogress_percentage: number) => {
    if (progprogress_percentage >= 80) return 'bg-green-500';
    if (progprogress_percentage >= 50) return 'bg-yellow-500';
    if (progprogress_percentage >= 20) return 'bg-orange-500';
    return 'bg-red-500';
  };


  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'completion': return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'start': return <Play className="w-4 h-4 text-blue-500" />;
      case 'pause': return <Pause className="w-4 h-4 text-yellow-500" />;
      case 'submission': return <Send className="w-4 h-4 text-purple-500" />;
      case 'login': return <User className="w-4 h-4 text-gray-500" />;
      case 'progress': return <TrendingUp className="w-4 h-4 text-orange-500" />;
      default: return <Activity className="w-4 h-4 text-gray-500" />;
    }
  };

  const sendEmail = async () => {
    if (!userInfo || !courseInfo || !emailContent) return;

    try {
      const emailData = emailService.createCustomEmail(
        userInfo.id,
        userInfo.email,
        getCurrentUserId(),
        `Course Progress Update - ${courseInfo.title}`,
        emailContent,
        {
          courseId: courseInfo.id,
          courseTitle: courseInfo.title,
          currentProgress: userCourseData?.overallProgress || 0,
          daysUntilDeadline: pace?.daysUntilDeadline,
          currentGrade: userCourseData?.grade
        }
      );

      await emailService.sendEmail(emailData);
      toast.success('Email sent successfully');
      setShowEmailModal(false);
      setEmailContent('');
    } catch (error: any) {
      toast.error(error.message || 'Failed to send email');
    }
  };

  const calculatePace = (): PaceCalculation | null => {
    if (!userCourseData || !courseInfo) return null;

    const remainingLessons = userCourseData.totalLessons - userCourseData.completedLessons;
    const daysUntilDeadline = courseInfo.deadlineRaw
      ? Math.ceil((new Date(courseInfo.deadlineRaw).getTime() - Date.now()) / (1000 * 60 * 60 * 24))
      : 90;
    const lessonsPerDay = daysUntilDeadline > 0 ? remainingLessons / daysUntilDeadline : remainingLessons;

    return {
      remainingLessons,
      daysUntilDeadline,
      recommendedPerDay: Math.ceil(lessonsPerDay),
      status: lessonsPerDay > 5 ? 'behind' : lessonsPerDay > 3 ? 'on-track' : 'ahead'
    };
  };

  const pace = calculatePace();

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        {/* Header Skeleton */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-start justify-between mb-6">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 bg-gray-200 rounded-lg"></div>
              <div>
                <div className="h-7 w-64 bg-gray-200 rounded mb-2"></div>
                <div className="h-4 w-48 bg-gray-200 rounded"></div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="h-10 w-10 bg-gray-200 rounded-lg"></div>
              <div className="h-10 w-10 bg-gray-200 rounded-lg"></div>
            </div>
          </div>

          {/* User and Course Info Cards Skeleton */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* User Info Card Skeleton */}
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 bg-gray-200 rounded-full"></div>
                <div className="flex-1 space-y-2">
                  <div className="h-5 w-40 bg-gray-200 rounded"></div>
                  <div className="h-4 w-52 bg-gray-200 rounded"></div>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    <div className="h-4 w-24 bg-gray-200 rounded"></div>
                    <div className="h-4 w-20 bg-gray-200 rounded"></div>
                  </div>
                </div>
              </div>
            </div>

            {/* Course Info Card Skeleton */}
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 bg-gray-200 rounded-lg"></div>
                <div className="flex-1 space-y-2">
                  <div className="h-5 w-44 bg-gray-200 rounded"></div>
                  <div className="h-4 w-36 bg-gray-200 rounded"></div>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    <div className="h-4 w-28 bg-gray-200 rounded"></div>
                    <div className="h-4 w-24 bg-gray-200 rounded"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Statistics Cards Skeleton */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="bg-gradient-to-br from-gray-50 to-gray-100 border border-gray-200 rounded-xl p-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-9 h-9 bg-gray-200 rounded-lg"></div>
                  <div className="h-4 w-24 bg-gray-200 rounded"></div>
                </div>
                <div className="h-8 w-16 bg-gray-200 rounded mb-3"></div>
                <div className="h-2 w-full bg-gray-200 rounded"></div>
              </div>
            ))}
          </div>
        </div>

        {/* Module List Skeleton */}
        <div className="bg-white rounded-lg shadow-sm">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <div className="h-6 w-40 bg-gray-200 rounded mb-2"></div>
                <div className="h-4 w-56 bg-gray-200 rounded"></div>
              </div>
              <div className="flex items-center gap-2">
                <div className="h-10 w-32 bg-gray-200 rounded-lg"></div>
                <div className="h-10 w-28 bg-gray-200 rounded-lg"></div>
              </div>
            </div>
          </div>

          <div className="divide-y divide-gray-200">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4 flex-1">
                    <div className="w-10 h-10 bg-gray-200 rounded-lg"></div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="h-5 w-48 bg-gray-200 rounded"></div>
                        <div className="h-5 w-16 bg-gray-200 rounded-full"></div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="h-3 w-24 bg-gray-200 rounded"></div>
                        <div className="h-3 w-20 bg-gray-200 rounded"></div>
                        <div className="h-2 w-32 bg-gray-200 rounded-full"></div>
                      </div>
                    </div>
                    <div className="w-8 h-8 bg-gray-200 rounded-lg"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto" />
          <p className="mt-4 text-red-600">{error}</p>
          <button
            onClick={handleBackNavigation}
            className="mt-4 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm">
        <div className="p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4 sm:mb-6">
            <div className="flex items-center gap-2 sm:gap-4">
              <button
                onClick={handleBackNavigation}
                className="p-1.5 sm:p-2 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
                title="Go back"
              >
                <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
              <div className="min-w-0">
                <h1 className="text-lg sm:text-2xl font-bold text-gray-900 truncate">Individual Course Progress</h1>
                <p className="text-xs sm:text-sm text-gray-500 mt-0.5 sm:mt-1 truncate">Detailed progress tracking for {userInfo?.name}</p>
              </div>
            </div>
            <div className="flex items-center self-end sm:self-auto flex-shrink-0 gap-2">
              <button
                onClick={openEditModal}
                className="h-9 sm:h-10 px-3 sm:px-4 min-w-[90px] sm:min-w-[180px] bg-indigo-600 text-white text-xs sm:text-sm rounded-lg hover:bg-indigo-700 transition-colors flex items-center justify-center gap-1.5 sm:gap-2"
              >
                <Edit2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                <span className="hidden sm:inline">Edit Settings</span>
                <span className="sm:hidden">Edit</span>
              </button>
              <button
                onClick={() => setShowRevokeModal(true)}
                className="h-9 sm:h-10 px-3 sm:px-4 min-w-[90px] sm:min-w-[180px] bg-red-600 text-white text-xs sm:text-sm rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center gap-1.5 sm:gap-2"
              >
                <XCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                <span className="hidden sm:inline">Revoke Course</span>
                <span className="sm:hidden">Revoke</span>
              </button>
            </div>
            {/* <div className="flex items-center gap-2">
              <button
                onClick={() => setShowActivityLog(!showActivityLog)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors flex items-center gap-2"
              >
                <Activity className="w-4 h-4" />
                Activity Log
              </button>
              <button
                onClick={() => setShowEmailModal(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
              >
                <Mail className="w-4 h-4" />
                Send Email
              </button>
            </div> */}
          </div>

          {/* User and Course Info */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6">
            {/* User Info Card */}
            <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
              <div className="flex items-start gap-3 sm:gap-4">
                <div className="w-10 h-10 sm:w-16 sm:h-16 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <User className="w-5 h-5 sm:w-8 sm:h-8 text-blue-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-gray-900 text-sm sm:text-base truncate">{userInfo?.name}</h3>
                  <p className="text-xs sm:text-sm text-gray-500 truncate">{userInfo?.email}</p>
                  <div className="mt-2 flex flex-wrap items-center gap-2 sm:gap-4 text-sm">
                    {/* Departments */}
                    {(() => {
                      const departments = userInfo?.departments || (userInfo?.department ? [userInfo.department] : []);
                      if (departments.length === 0) {
                        return null;
                      }

                      if (departments.length === 1) {
                        return (
                          <span className="flex items-center gap-1">
                            <Building className="w-4 h-4 text-gray-400" />
                            <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full">
                              {departments[0]}
                            </span>
                          </span>
                        );
                      }

                      return (
                        <span className="flex items-center gap-2">
                          <Building className="w-4 h-4 text-gray-400" />
                          <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full">
                            {departments[0]}
                          </span>
                          <button
                            onClick={() => setShowDepartmentsModal(true)}
                            className="text-xs text-blue-600 font-medium hover:text-blue-800 hover:underline"
                          >
                            +{departments.length - 1} more
                          </button>
                        </span>
                      );
                    })()}

                    {/* Roles */}
                    {(() => {
                      const roles = userInfo?.roles || (userInfo?.role ? [userInfo.role] : []);

                      if (roles.length === 0) {
                        return null;
                      }

                      if (roles.length === 1) {
                        return (
                          <span className="flex items-center gap-1">
                            <Briefcase className="w-4 h-4 text-gray-400" />
                            <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-600 rounded-full">
                              {roles[0]}
                            </span>
                          </span>
                        );
                      }

                      return (
                        <span className="flex items-center gap-2">
                          <Briefcase className="w-4 h-4 text-gray-400" />
                          <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-600 rounded-full">
                            {roles[0]}
                          </span>
                          <button
                            onClick={() => setShowRolesModal(true)}
                            className="text-xs text-blue-600 font-medium hover:text-blue-800 hover:underline"
                          >
                            +{roles.length - 1} more
                          </button>
                        </span>
                      );
                    })()}
                  </div>
                  <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-xs sm:text-sm">
                    <div><span className="text-gray-500">Joined:</span> <span className="font-medium">{userInfo?.joinedDate || 'N/A'}</span></div>
                    <div><span className="text-gray-500">Last Active:</span> <span className="font-medium">{userInfo?.lastActive || 'N/A'}</span></div>
                  </div>
                </div>
              </div>
            </div>

            {/* Course Info Card */}
            <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
              <div className="flex items-start gap-3 sm:gap-4">
                <div className="w-10 h-10 sm:w-16 sm:h-16 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
                  <BookOpen className="w-5 h-5 sm:w-8 sm:h-8 text-purple-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-semibold text-gray-900 text-sm sm:text-base">
                      {courseInfo?.title && courseInfo.title.length > TITLE_CHAR_LIMIT ? (
                        <>
                          {courseInfo.title.slice(0, TITLE_CHAR_LIMIT)}...
                          <button
                            onClick={() => setShowTitleModal(true)}
                            className="ml-1 text-xs font-medium text-blue-600 hover:text-blue-700 hover:underline"
                          >
                            show more
                          </button>
                        </>
                      ) : (
                        courseInfo?.title
                      )}
                    </h3>
                    {(courseInfo as any)?.is_mandatory ? (
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-semibold rounded-full bg-gradient-to-r from-red-50 to-rose-50 text-red-700 border border-red-200 shadow-sm">
                        <span className="w-2 h-2 rounded-full bg-red-500"></span>
                        Mandatory
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-semibold rounded-full bg-gradient-to-r from-slate-50 to-gray-50 text-slate-600 border border-slate-200 shadow-sm">
                        <span className="w-2 h-2 rounded-full bg-slate-400"></span>
                        Optional
                      </span>
                    )}
                  </div>
                  {/* <p className="text-sm text-gray-500">
                    Instructor: {typeof courseInfo?.instructor === 'string' 
                      ? courseInfo.instructor 
                      : courseInfo?.instructor?.name || 'Unknown Instructor'}
                  </p> */}
                  <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-xs sm:text-sm">
                    <div><span className="text-gray-500">Category:</span> <span className="font-medium">{courseInfo?.category}</span></div>
                    <div><span className="text-gray-500">Difficulty:</span> <span className="font-medium">{courseInfo?.difficulty}</span></div>
                    <div>
                      <span className="text-gray-500">Duration:</span>
                      <span className="font-medium">
                        {typeof courseInfo?.duration === 'string' ? courseInfo.duration : courseInfo?.duration?.total || 'N/A'}
                      </span>
                    </div>
                    {/* <div><span className="text-gray-500">Passing Score:</span> <span className="font-medium">{courseInfo?.passingScore}%</span></div> */}
                  </div>
                  {courseInfo?.deadline && (() => {
                    const isTomorrow = pace?.daysUntilDeadline === 1;
                    const deadlineColor = isTomorrow ? 'text-red-600' : 'text-blue-600';
                    const iconColor = isTomorrow ? 'text-red-500' : 'text-blue-500';
                    return (
                    <div className="mt-2 flex items-center gap-2">
                      <AlertCircle className={`w-4 h-4 ${iconColor}`} />
                      <span className="text-sm">
                        Deadline: <span className={`font-medium ${deadlineColor}`}>{courseInfo.deadline}</span>
                        {pace?.daysUntilDeadline !== undefined && pace.daysUntilDeadline > 0 && (
                          <span className={`ml-2 ${deadlineColor}`}>({pace.daysUntilDeadline} days remaining)</span>
                        )}
                        {pace?.daysUntilDeadline !== undefined && pace.daysUntilDeadline < 0 && (
                          <span className="text-red-500 ml-2">(Overdue)</span>
                        )}
                        {pace?.daysUntilDeadline === 0 && (
                          <span className="text-orange-500 ml-2">(Due today)</span>
                        )}
                      </span>
                    </div>
                    );
                  })()}
                </div>
              </div>
            </div>
          </div>

          {/* Progress Overview - Bound to metrics API */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
            {/* Overall Progress Card */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-3 sm:p-5 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-20 h-20 bg-blue-100 rounded-full opacity-30 -translate-y-8 translate-x-8"></div>

              <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3 relative z-10">
                <div className="p-1.5 sm:p-2 bg-blue-100 rounded-lg">
                  <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                </div>
                <span className="text-xs sm:text-sm font-medium text-blue-900">Overall Progress</span>
              </div>

              <div className="relative z-10">
                <div className="flex items-end justify-between mb-2 sm:mb-3">
                  <div className="flex items-end gap-1 sm:gap-2">
                    <span className="text-xl sm:text-3xl font-bold text-blue-900">{courseOverview?.progress_percentage ?? 'N/A'}</span>
                    <span className="text-sm sm:text-lg font-medium text-blue-700 mb-0.5 sm:mb-1">%</span>
                  </div>
                  {courseOverview && (
                    <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                      courseOverview.status === 'COMPLETED' ? 'bg-green-100 text-green-700' :
                      courseOverview.status === 'IN_PROGRESS' ? 'bg-blue-100 text-blue-700' :
                      'bg-gray-100 text-gray-600'
                    }`}>
                      {courseOverview.status === 'COMPLETED' ? '✓ Complete' :
                       courseOverview.status === 'IN_PROGRESS' ? 'In Progress' :
                       courseOverview.status}
                    </span>
                  )}
                </div>

                <div className="relative">
                  <div className="h-3 bg-blue-200 rounded-full overflow-hidden shadow-inner">
                    <div
                      className="h-full bg-gradient-to-r from-blue-500 to-blue-600 rounded-full transition-all duration-1000 ease-out relative"
                      style={{ width: `${courseOverview?.progress_percentage ?? 0}%` }}
                    ></div>
                  </div>
                </div>

                <div className="mt-4 flex items-center justify-between text-sm">
                  <span className="text-blue-700">
                    {courseOverview?.completed_content_items ?? 'N/A'} / {courseOverview?.total_content_items ?? 'N/A'} lessons
                  </span>
                  <span className="text-blue-600 font-medium">
                    {(courseOverview?.total_content_items ?? 0) - (courseOverview?.completed_content_items ?? 0)} remaining
                  </span>
                </div>
              </div>
            </div>

            {/* Time Investment Card */}
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 border border-purple-200 rounded-xl p-3 sm:p-5 relative overflow-hidden">
              <div className="absolute bottom-0 left-0 w-16 h-16 bg-purple-100 rounded-full opacity-40 -translate-x-4 translate-y-4"></div>

              <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3 relative z-10">
                <div className="p-1.5 sm:p-2 bg-purple-100 rounded-lg">
                  <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600" />
                </div>
                <span className="text-xs sm:text-sm font-medium text-purple-900">Time Investment</span>
              </div>

              <div className="relative z-10">
                <div className="mb-2 sm:mb-3">
                  <div className="flex items-end gap-1 sm:gap-2">
                    <span className="text-lg sm:text-3xl font-bold text-purple-900">
                      {courseOverview?.total_time_spent_seconds !== undefined ? formatTime(courseOverview.total_time_spent_seconds) : 'N/A'}
                    </span>
                    <span className="text-xs sm:text-sm text-purple-700 mb-0.5 sm:mb-1">total</span>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-purple-700">Modules Completed</span>
                    <span className="text-sm font-semibold text-purple-900">
                      {courseOverview?.completed_modules !== undefined ? `${courseOverview.completed_modules}/${courseOverview.total_modules}` : 'N/A'}
                    </span>
                  </div>

                  <div className="flex items-center gap-2 mt-3">
                    <div className="flex-1 h-2 bg-purple-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-purple-400 to-purple-600 rounded-full transition-all duration-700"
                        style={{ width: `${courseOverview && courseOverview.total_modules > 0 ? (courseOverview.completed_modules / courseOverview.total_modules) * 100 : 0}%` }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Assessment Score Card */}
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-xl p-3 sm:p-5 relative overflow-hidden">
              <div className="absolute -top-4 -right-4 w-16 h-16 bg-green-100 rounded-full opacity-40"></div>
              <div className="absolute bottom-2 left-2 w-10 h-10 bg-emerald-100 rounded-full opacity-50"></div>

              <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3 relative z-10">
                <div className="p-1.5 sm:p-2 bg-green-100 rounded-lg">
                  <Trophy className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
                </div>
                <span className="text-xs sm:text-sm font-medium text-green-900">Assessment Score</span>
              </div>

              <div className="relative z-10">
                <div className="mb-2 sm:mb-3">
                  <div className="flex items-end gap-1 sm:gap-2">
                    <span className="text-xl sm:text-3xl font-bold text-green-900">
                      {courseOverview?.assessment_summary?.average_score?.toFixed(1) ?? 'N/A'}
                    </span>
                    <span className="text-sm sm:text-lg text-green-700 mb-0.5 sm:mb-1">%</span>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-green-700">Completed</span>
                    <span className="text-sm font-semibold text-green-900">
                      {courseOverview?.assessment_summary ? `${courseOverview.assessment_summary.completed_assessments}/${courseOverview.assessment_summary.total_assessments}` : 'N/A'}
                    </span>
                  </div>

                  <div className="relative">
                    <div className="h-2 bg-green-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-green-400 to-green-600 rounded-full transition-all duration-700"
                        style={{ width: `${courseOverview?.assessment_summary?.average_score ?? 0}%` }}
                      />
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-xs text-green-700">Pass/Fail</span>
                    <span className="text-xs font-medium">
                      <span className="text-green-600">{courseOverview?.assessment_summary?.passed_count ?? 0} passed</span>
                      {' / '}
                      <span className="text-red-600">{courseOverview?.assessment_summary?.failed_count ?? 0} failed</span>
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* XP & Level Card */}
            <div className="bg-gradient-to-br from-orange-50 to-yellow-50 border border-orange-200 rounded-xl p-3 sm:p-5 relative overflow-hidden">
              <div className="absolute top-2 right-2 w-12 h-12 bg-orange-100 rounded-full opacity-50"></div>
              <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-yellow-100 rounded-full opacity-60"></div>

              <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3 relative z-10">
                <div className="p-1.5 sm:p-2 bg-orange-100 rounded-lg">
                  <Zap className="w-4 h-4 sm:w-5 sm:h-5 text-orange-600" />
                </div>
                <span className="text-xs sm:text-sm font-medium text-orange-900">XP & Level</span>
              </div>

              <div className="relative z-10">
                <div className="flex items-end justify-between mb-2 sm:mb-3">
                  <div className="flex items-end gap-1 sm:gap-2">
                    <span className="text-xl sm:text-3xl font-bold text-orange-900">{courseOverview?.xp_details?.xp ?? 'N/A'}</span>
                    <span className="text-xs sm:text-sm text-orange-700 mb-0.5 sm:mb-1">XP</span>
                  </div>
                  {courseOverview?.xp_details && (
                    <span className="text-xs px-2 py-1 bg-orange-100 text-orange-700 rounded-full font-medium">
                      Level {courseOverview.xp_details.level}
                    </span>
                  )}
                </div>

                <div className="flex items-center gap-2 mt-4">
                  <div className="w-2 h-2 bg-orange-500 rounded-full animate-pulse"></div>
                  <span className="text-sm text-orange-700">
                    {courseOverview?.xp_details?.streak ?? 0} day streak
                  </span>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* Terms & Conditions acceptance */}
      {isTermsRequired && termsAcceptances.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm mb-4 sm:mb-6">
          <div className="p-4 sm:p-6 border-b border-gray-200">
            <h2 className="text-base sm:text-xl font-semibold">Terms &amp; Conditions</h2>
            <p className="text-xs sm:text-sm text-gray-500 mt-1">Learner agreement status for this course</p>
          </div>
          <div className="p-4 sm:p-6 space-y-3">
            {termsAcceptances.map((r) => (
              <div
                key={r.condition_id}
                className="flex items-center gap-3 sm:gap-4 p-3 rounded-xl border border-gray-100 bg-gray-50"
              >
                <div
                  className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    r.accepted ? 'bg-green-100' : 'bg-amber-100'
                  }`}
                >
                  <FileText className={`w-5 h-5 ${r.accepted ? 'text-green-600' : 'text-amber-600'}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{r.title}</p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {r.accepted && r.accepted_at
                      ? `Agreed on ${formatDate(r.accepted_at)} at ${new Date(r.accepted_at).toLocaleTimeString()}`
                      : 'Not yet accepted'}
                    {/* {r.accepted && r.accepted_version ? ` · v${r.accepted_version}` : ''} */}
                  </p>
                  {/* {r.accepted && r.ip_address && (
                    <p className="text-[11px] text-gray-400 mt-0.5">IP {r.ip_address}</p>
                  )} */}
                </div>
                {r.accepted ? (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700 flex-shrink-0">
                    <CheckCircle className="w-3.5 h-3.5" /> Accepted
                  </span>
                ) : (
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700 flex-shrink-0">
                    Pending
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Course Skeleton with User Progress */}
      <div className="bg-white rounded-lg shadow-sm">
        <div className="p-4 sm:p-6 border-b border-gray-200">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0">
            <div>
              <h2 className="text-base sm:text-xl font-semibold">Course Progress Details</h2>
            </div>
            <div className="flex items-center gap-2">
              <SimpleSelect
                value={filterStatus}
                onChange={(value) => setFilterStatus((value || 'all') as 'all' | 'completed' | 'in-progress' | 'not-started')}
                options={filterOptions}
                isSearchable={false}
              />
              {/* <button
                onClick={() => setViewMode(viewMode === 'detailed' ? 'compact' : 'detailed')}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50"
              >
                {viewMode === 'detailed' ? 'Compact View' : 'Detailed View'}
              </button> */}
            </div>
          </div>
        </div>

        <div className="divide-y divide-gray-200">
          {modules.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <BookOpen className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p>No modules found for this course</p>
              <p className="text-sm mt-1">Debug: modules.length = {modules.length}</p>
            </div>
          ) : null}
          {modules.map((module) => (
            <div key={module.id} className="transition-all duration-200">
              {/* Module Header */}
              <div
                className={`p-3 sm:p-4 cursor-pointer hover:bg-gray-50 ${
                  module.isLocked ? 'opacity-60' : ''
                }`}
                onClick={() => !module.isLocked && toggleModule(module.id)}
              >
                <div className="flex items-center gap-2 sm:gap-4">
                  <div className="flex-shrink-0">
                    {module.isLocked ? (
                      <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gray-200 flex items-center justify-center">
                        <Lock className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500" />
                      </div>
                    ) : module.progress === 100 ? (
                      <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-green-100 flex items-center justify-center">
                        <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" />
                      </div>
                    ) : (
                      <div className="relative w-8 h-8 sm:w-10 sm:h-10">
                        <svg className="w-8 h-8 sm:w-10 sm:h-10 transform -rotate-90" viewBox="0 0 40 40">
                          <circle
                            cx="20"
                            cy="20"
                            r="18"
                            stroke="#E5E7EB"
                            strokeWidth="4"
                            fill="none"
                          />
                          <circle
                            cx="20"
                            cy="20"
                            r="18"
                            stroke="#3B82F6"
                            strokeWidth="4"
                            fill="none"
                            strokeDasharray={`${(module.progress / 100) * 113} 113`}
                          />
                        </svg>
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="text-[10px] sm:text-xs font-semibold">{module.progress}%</span>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-1.5 sm:gap-3">
                      <h3 className="font-semibold text-gray-900 text-sm sm:text-base truncate">{module.title}</h3>
                      {/* Only count content items as lessons, not assessments */}
                      {module.lessons.filter(l => l.type !== 'assessment' && l.type !== 'quiz').length > 0 && (
                        <span className="text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 sm:py-1 bg-gray-100 text-gray-600 rounded-full">
                          {module.lessons.filter(l => l.type !== 'assessment' && l.type !== 'quiz').length} lessons
                        </span>
                      )}
                      <span className="hidden sm:inline text-xs px-2 py-1 bg-blue-50 text-blue-600 rounded-full">
                        Time spent: {formatTime(Number(module.timeSpent || 0))}
                      </span>
                    </div>
                    <div className="mt-1.5 sm:mt-2 flex flex-wrap items-center gap-2 sm:gap-4">
                      <div className="flex-1 min-w-[80px] max-w-xs">
                        <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className={`h-full transition-all duration-300 ${getProgressColor(module.progress_percentage)}`}
                            style={{ width: `${module.progress_percentage}%` }}
                          />
                        </div>
                      </div>
                      {/* Only count content items for completion, not assessments - hide if no content items */}
                      {module.lessons.filter(l => l.type !== 'assessment' && l.type !== 'quiz').length > 0 && (
                        <span className="text-xs sm:text-sm text-gray-500">
                          {module.lessons.filter(l => l.type !== 'assessment' && l.type !== 'quiz' && l.isCompleted).length}/
                          {module.lessons.filter(l => l.type !== 'assessment' && l.type !== 'quiz').length} completed
                        </span>
                      )}
                      <span className="sm:hidden text-[10px] text-blue-600">
                        {formatTime(Number(module.timeSpent || 0))}
                      </span>
                      {module.startedAt && (
                        <span className="hidden sm:inline text-xs text-gray-500">
                          Started: {formatDate(module.startedAt)}
                        </span>
                      )}
                      {module.completedAt && (
                        <span className="hidden sm:inline text-xs text-green-600">
                          Completed: {formatDate(module.completedAt)}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex-shrink-0">
                    {!module.isLocked && (
                      expandedModules.includes(module.id) ? (
                        <ChevronUp className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                      ) : (
                        <ChevronDown className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                      )
                    )}
                  </div>
                </div>
              </div>

              {/* Module Lessons */}
              {expandedModules.includes(module.id) && !module.isLocked && (
                <div className="bg-gray-50 border-t border-gray-200">
                  <div className="divide-y divide-gray-200">
                    {module.lessons
                      .filter((lesson) => {
                        if (filterStatus === 'all') return true;
                        if (filterStatus === 'completed') return lesson.isCompleted;
                        if (filterStatus === 'in-progress') return !lesson.isCompleted && lesson.progress && lesson.progress > 0;
                        if (filterStatus === 'not-started') return !lesson.isCompleted && (!lesson.progress || lesson.progress === 0);
                        return true;
                      })
                      .map((lesson, index) => {
                      // Determine indent level
                      const isContentLevel = (lesson as any).indentLevel === 'content';
                      const indentClass = isContentLevel ? 'ml-8' : 'ml-0';
                      const borderClass = isContentLevel ? 'border-l-2 border-blue-200 pl-4' : '';

                      return (
                        <div
                          key={lesson.id}
                          className={`${viewMode === 'compact' ? 'px-3 sm:px-6 py-2' : 'px-3 sm:px-6 py-2 sm:py-3'} hover:bg-white transition-colors ${
                            lesson.isLocked ? 'opacity-60' : 'cursor-pointer'
                          }`}
                          onClick={() => !lesson.isLocked && setSelectedLesson(lesson)}
                        >
                          {viewMode === 'compact' ? (
                            // Compact View Layout
                            <div className={`flex items-center gap-3 ${indentClass} ${borderClass}`}>
                              <div className="flex-shrink-0 text-xs text-gray-400 w-6">
                                {String(index + 1).padStart(2, '0')}
                              </div>
                              
                              <div className="flex-shrink-0">
                                {lesson.isCompleted ? (
                                  <CheckCircle className="w-4 h-4 text-green-500" />
                                ) : lesson.isLocked ? (
                                  <Lock className="w-3 h-3 text-gray-400" />
                                ) : lesson.progress && lesson.progress > 0 ? (
                                  <div className="w-4 h-4 rounded-full bg-orange-100 flex items-center justify-center">
                                    <div className="w-2 h-2 rounded-full bg-orange-500" />
                                  </div>
                                ) : (
                                  <div className="w-4 h-4 rounded-full border border-gray-300" />
                                )}
                              </div>

                              <div className="flex-shrink-0">
                                {getLessonIcon(lesson.type)}
                              </div>

                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <span className={`text-sm truncate ${lesson.isCompleted ? 'text-gray-600 line-through' : 'text-gray-900'}`}>
                                    {lesson.title}
                                  </span>
                                  {renderMandatoryLabel(lesson)}
                                  {lesson.bookmarked && (
                                    <Bookmark className="w-3 h-3 text-yellow-500 fill-current" />
                                  )}
                                </div>
                              </div>

                              <div className="flex-shrink-0 flex items-center gap-3">
                                {lesson.progress && lesson.progress > 0 && !lesson.isCompleted && (
                                  <span className="text-xs text-orange-600 font-medium">
                                    {lesson.progress}%
                                  </span>
                                )}
                                {lesson.score && (
                                  <span className={`text-xs font-medium ${lesson.score >= 80 ? 'text-green-600' : 'text-orange-600'}`}>
                                    {lesson.score}%
                                  </span>
                                )}
                                <span className="text-xs text-gray-500">{lesson.duration}</span>
                              </div>
                            </div>
                          ) : (
                            // Detailed View Layout
                            <div className={`flex items-start gap-2 sm:gap-4 ${indentClass} ${borderClass}`}>
                              <div className="flex-shrink-0 text-gray-400 mt-1 text-xs sm:text-sm">
                                {String(index + 1).padStart(2, '0')}
                              </div>

                              <div className="flex-shrink-0 mt-1">
                                {lesson.isCompleted ? (
                                  <CheckCircle className="w-5 h-5 text-green-500" />
                                ) : lesson.isLocked ? (
                                  <Lock className="w-4 h-4 text-gray-400" />
                                ) : lesson.progress ? (
                                  <div className="relative w-5 h-5">
                                    <svg className="w-5 h-5 transform -rotate-90">
                                      <circle
                                        cx="10"
                                        cy="10"
                                        r="8"
                                        stroke="#E5E7EB"
                                        strokeWidth="2"
                                        fill="none"
                                      />
                                      <circle
                                        cx="10"
                                        cy="10"
                                        r="8"
                                        stroke="#F59E0B"
                                        strokeWidth="2"
                                        fill="none"
                                        strokeDasharray={`${(lesson.progress / 100) * 50} 50`}
                                      />
                                    </svg>
                                  </div>
                                ) : (
                                  <div className="w-5 h-5 rounded-full border-2 border-gray-300" />
                                )}
                              </div>

                              <div className="flex-shrink-0 mt-1">
                                {getLessonIcon(lesson.type)}
                              </div>

                              <div className="flex-1">
                                <div className="flex items-start justify-between">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2">
                                      <span className={`text-sm ${lesson.isCompleted ? 'text-gray-600 line-through' : 'text-gray-900'}`}>
                                        {lesson.title}
                                      </span>
                                      {renderMandatoryLabel(lesson)}
                                      {lesson.bookmarked && (
                                        <Bookmark className="w-3 h-3 text-yellow-500 fill-current" />
                                      )}
                                      {/* {lesson.progress && !lesson.isCompleted && (
                                        <span className="text-xs text-orange-600 font-medium">
                                          {lesson.progress}% complete
                                        </span>
                                      )} */}
                                    </div>
                                    
                                    {viewMode === 'detailed' && (
                                      <div className="mt-1 space-y-1">
                                        <div className="flex flex-wrap items-center gap-x-3 sm:gap-x-4 gap-y-0.5 text-[10px] sm:text-xs text-gray-500">
                                          {lesson.startedAt && (
                                            <span>Started: {formatDate(lesson.startedAt)}</span>
                                          )}
                                          {lesson.completedAt && (
                                            <span className="text-green-600">Completed: {formatDate(lesson.completedAt)}</span>
                                          )}
                                          {lesson.lastAccessed && (
                                            <span className="hidden sm:inline">Last accessed: {formatDate(lesson.lastAccessed)}</span>
                                          )}
                                          {lesson.timeSpent && lesson.timeSpent !== '00:00:00' && (
                                            <span>Time Spent: {lesson.timeSpent}</span>
                                          )}
                                        </div>

                                        {(lesson.type === 'quiz' || lesson.type === 'assessment') && lesson.quiz && (
                                          <div className="flex items-center gap-2 sm:gap-4 flex-wrap">
                                            <span className="text-[10px] sm:text-xs">
                                              Best Score: <strong className={lesson.quiz.passed ? 'text-green-600' : (lesson.quiz.score || 0) >= (lesson.quiz.passPercentage || 70) ? 'text-green-600' : 'text-red-600'}>
                                                {lesson.quiz.bestScore || 0} ({(lesson.quiz.score || 0).toFixed(2)}%)
                                              </strong>
                                            </span>
                                            {lesson.quiz.attempts !== undefined && (
                                              <span className="text-[10px] sm:text-xs text-gray-500">
                                                Attempts: {lesson.quiz.attempts}
                                              </span>
                                            )}
                                            {lesson.quiz.passPercentage && (
                                              <span className="hidden sm:inline text-xs text-gray-500">
                                                Passing: {lesson.quiz.passPercentage}%
                                              </span>
                                            )}
                                            {lesson.quiz.attempts !== undefined && lesson.quiz.attempts > 0 ? (
                                              <span className={`text-xs px-2 py-0.5 rounded-full ${lesson.quiz.passed ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                                {lesson.quiz.passed ? '✓ Passed' : '✗ Failed'}
                                              </span>
                                            ) : (
                                              <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                                                Not Attempted
                                              </span>
                                            )}
                                            {/* View Attempts Button */}
                                            {lesson.attempts_detail && lesson.attempts_detail.length > 0 && (
                                              <button
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  setSelectedAssessment(lesson);
                                                  setShowAttemptsModal(true);
                                                }}
                                                className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 hover:bg-blue-200 transition-colors flex items-center gap-1"
                                              >
                                                <Eye className="w-3 h-3" />
                                                View Attempts
                                              </button>
                                            )}
                                          </div>
                                        )}

                                        {lesson.notes && (
                                          <div className="flex items-start gap-2">
                                            <MessageSquare className="w-3 h-3 text-gray-400 mt-0.5" />
                                            <p className="text-xs text-gray-600 italic">{lesson.notes}</p>
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>

                                  {/* <div className="flex-shrink-0 flex items-center gap-2 ml-4">
                                    <span className="text-sm text-gray-500">{lesson.duration}</span>
                                    {!lesson.isLocked && lesson.progress && lesson.progress < 100 && (
                                      <button className="text-blue-600 hover:text-blue-700">
                                        <Play className="w-4 h-4" />
                                      </button>
                                    )}
                                    {lesson.isCompleted && (
                                      <button className="text-green-600 hover:text-green-700">
                                        <RefreshCw className="w-4 h-4" />
                                      </button>
                                    )}
                                  </div> */}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                    {/* Show message when no lessons match the filter */}
                    {module.lessons.filter((lesson) => {
                      if (filterStatus === 'all') return true;
                      if (filterStatus === 'completed') return lesson.isCompleted;
                      if (filterStatus === 'in-progress') return !lesson.isCompleted && lesson.progress && lesson.progress > 0;
                      if (filterStatus === 'not-started') return !lesson.isCompleted && (!lesson.progress || lesson.progress === 0);
                      return true;
                    }).length === 0 && (
                      <div className="px-6 py-4 text-center text-gray-500 text-sm">
                        No lessons match the selected filter
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Activity Log */}
      {showActivityLog && (
        <div className="bg-white rounded-lg shadow-sm">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-semibold">Recent Activity</h3>
            <p className="text-sm text-gray-500 mt-1">User's learning activity timeline</p>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              {activityLog.map((activity, index) => (
                <div key={index} className="flex items-start gap-3">
                  <div className="flex-shrink-0 mt-0.5">
                    {getActivityIcon(activity.type)}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-900">{activity.action}</span>
                      <span className="text-xs text-gray-500">{activity.date || activity.timestamp}</span>
                    </div>
                    <p className="text-sm text-gray-600 mt-0.5">
                      {typeof activity.details === 'string' ? activity.details : JSON.stringify(activity.details)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Email Modal */}
      <Modal
        isOpen={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        title={`Send Email to ${userInfo?.name}`}
        size="lg"
        maxHeight="80vh"
        footer={
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setShowEmailModal(false)}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={sendEmail}
              disabled={!emailContent}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <Send className="w-4 h-4" />
              Send Email
            </button>
          </div>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Quick Templates
            </label>
            <div className="grid grid-cols-3 gap-2">
              <button
                onClick={() => setEmailContent('Great progress on the course! Keep up the excellent work.')}
                className="p-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Encouragement
              </button>
              <button
                onClick={() => setEmailContent(`You're falling behind on the course schedule. The deadline is ${courseInfo?.deadline}. Let me know if you need any help.`)}
                className="p-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Reminder
              </button>
              <button
                onClick={() => setEmailContent('I noticed you haven\'t accessed the course recently. Is there anything I can help you with?')}
                className="p-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Check-in
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Message
            </label>
            <textarea
              value={emailContent}
              onChange={(e) => setEmailContent(e.target.value)}
              rows={6}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Type your message here..."
            />
          </div>

          <div className="p-3 bg-gray-50 rounded-lg text-sm text-gray-600">
            <strong>Context:</strong> {userInfo?.name} is {userCourseData?.overallProgress}% through "{courseInfo?.title}"
            with {pace?.daysUntilDeadline} days until deadline. Current grade: {userCourseData?.grade}%
          </div>
        </div>
      </Modal>

      {/* Departments Modal */}
      {userInfo && (
        <Modal
          isOpen={showDepartmentsModal}
          onClose={() => setShowDepartmentsModal(false)}
          title="Department Details"
          subtitle={`${userInfo.name}`}
          size="md"
          maxHeight="80vh"
          footer={
            <div className="flex items-center justify-end">
              <button
                onClick={() => setShowDepartmentsModal(false)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Close
              </button>
            </div>
          }
        >
          <div className="space-y-3">
            {(userInfo.departments || (userInfo.department ? [userInfo.department] : [])).map((dept, idx) => (
              <div key={`dept-${idx}`} className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="bg-gray-50 px-4 py-3 flex items-center gap-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-semibold text-sm">
                    {idx + 1}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-sm font-medium text-gray-900">{dept}</h3>
                  </div>
                  <Building className="w-5 h-5 text-gray-500" />
                </div>
              </div>
            ))}
          </div>
        </Modal>
      )}

      {/* Roles Modal */}
      {userInfo && (
        <Modal
          isOpen={showRolesModal}
          onClose={() => setShowRolesModal(false)}
          title="Role Details"
          subtitle={`${userInfo.name}`}
          size="md"
          maxHeight="80vh"
          footer={
            <div className="flex items-center justify-end">
              <button
                onClick={() => setShowRolesModal(false)}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
              >
                Close
              </button>
            </div>
          }
        >
          <div className="space-y-3">
            {(userInfo.roles || (userInfo.role ? [userInfo.role] : [])).map((role, idx) => (
              <div key={`role-${idx}`} className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="bg-gray-50 px-4 py-3 flex items-center gap-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center font-semibold text-sm">
                    {idx + 1}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-sm font-medium text-gray-900">{role}</h3>
                  </div>
                  <Briefcase className="w-5 h-5 text-gray-500" />
                </div>
              </div>
            ))}
          </div>
        </Modal>
      )}

      {/* Assessment Attempts Modal */}
      {selectedAssessment && (
        <Modal
          isOpen={showAttemptsModal}
          onClose={() => {
            setShowAttemptsModal(false);
            setSelectedAssessment(null);
            setSelectedAttempt(null);
          }}
          title={selectedAttempt ? `Attempt ${selectedAttempt.attempt_number} Details` : 'Assessment Attempts'}
          subtitle={selectedAssessment.title}
          size="xl"
          maxHeight="90vh"
          footer={
            <div className="flex flex-col-reverse sm:flex-row items-center justify-between gap-2">
              {selectedAttempt ? (
                <button
                  onClick={() => setSelectedAttempt(null)}
                  className="w-full sm:w-auto px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Back to Attempts
                </button>
              ) : (
                <div className="hidden sm:block"></div>
              )}
              <button
                onClick={() => {
                  setShowAttemptsModal(false);
                  setSelectedAssessment(null);
                  setSelectedAttempt(null);
                }}
                className="w-full sm:w-auto px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Close
              </button>
            </div>
          }
        >
          {!selectedAttempt ? (
            /* Attempts List View */
            <div className="space-y-4">
              {/* Assessment Summary */}
              <div className="bg-gray-50 rounded-lg p-3 sm:p-4 grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
                <div>
                  <p className="text-xs text-gray-500">Total Attempts</p>
                  <p className="text-base sm:text-lg font-semibold text-gray-900">{selectedAssessment.attempts || 0}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Best Score</p>
                  <p className="text-base sm:text-lg font-semibold text-gray-900">{selectedAssessment.quiz?.bestScore || 0}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Passing Score</p>
                  <p className="text-base sm:text-lg font-semibold text-gray-900">{selectedAssessment.quiz?.passPercentage || 0}%</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Status</p>
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                    selectedAssessment.quiz?.passed
                      ? 'bg-green-100 text-green-700'
                      : 'bg-red-100 text-red-700'
                  }`}>
                    {selectedAssessment.quiz?.passed ? '✓ Passed' : '✗ Not Passed'}
                  </span>
                </div>
              </div>

              {/* Attempts List */}
              {selectedAssessment.attempts_detail && selectedAssessment.attempts_detail.length > 0 ? (
                <div className="space-y-3">
                  {selectedAssessment.attempts_detail.map((attempt) => (
                    <div
                      key={attempt.attempt_id}
                      className="border border-gray-200 rounded-lg p-3 sm:p-4 hover:border-blue-300 hover:bg-blue-50/30 cursor-pointer transition-all"
                      onClick={() => setSelectedAttempt(attempt)}
                    >
                      {/* Mobile Layout */}
                      <div className="flex flex-col sm:hidden gap-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`w-9 h-9 rounded-full flex items-center justify-center font-semibold text-xs ${
                              attempt.passed
                                ? 'bg-green-100 text-green-700'
                                : 'bg-red-100 text-red-700'
                            }`}>
                              #{attempt.attempt_number}
                            </div>
                            <div>
                              <p className="font-medium text-gray-900 text-sm">Attempt {attempt.attempt_number}</p>
                              <p className="text-xs text-gray-500">
                                {formatDate(attempt.completed_at)}
                              </p>
                            </div>
                          </div>
                          <ChevronRight className="w-5 h-5 text-gray-400" />
                        </div>
                        <div className="flex items-center justify-between border-t border-gray-100 pt-2">
                          <div>
                            <p className="text-sm font-semibold text-gray-900">{attempt.score}/{attempt.max_score} ({attempt.percentage.toFixed(1)}%)</p>
                          </div>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            attempt.passed
                              ? 'bg-green-100 text-green-700'
                              : 'bg-red-100 text-red-700'
                          }`}>
                            {attempt.passed ? 'Passed' : 'Failed'}
                          </span>
                        </div>
                      </div>
                      {/* Desktop Layout */}
                      <div className="hidden sm:flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold text-sm ${
                            attempt.passed
                              ? 'bg-green-100 text-green-700'
                              : 'bg-red-100 text-red-700'
                          }`}>
                            #{attempt.attempt_number}
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">Attempt {attempt.attempt_number}</p>
                            <p className="text-sm text-gray-500">
                              {formatDate(attempt.completed_at)} at {new Date(attempt.completed_at).toLocaleTimeString()}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <p className="text-lg font-semibold text-gray-900">{attempt.score}/{attempt.max_score}</p>
                            <p className="text-sm text-gray-500">{attempt.percentage.toFixed(1)}%</p>
                          </div>
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                            attempt.passed
                              ? 'bg-green-100 text-green-700'
                              : 'bg-red-100 text-red-700'
                          }`}>
                            {attempt.passed ? 'Passed' : 'Failed'}
                          </span>
                          <ChevronRight className="w-5 h-5 text-gray-400" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <ClipboardCheck className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p>No attempts recorded yet</p>
                </div>
              )}
            </div>
          ) : (
            /* Attempt Details View with Questions */
            <div className="space-y-4">
              {/* Attempt Summary */}
              <div className="bg-gray-50 rounded-lg p-3 sm:p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                <div className="flex items-center gap-3 sm:gap-4">
                  <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center font-bold text-sm sm:text-base ${
                    selectedAttempt.passed
                      ? 'bg-green-100 text-green-700'
                      : 'bg-red-100 text-red-700'
                  }`}>
                    {selectedAttempt.percentage.toFixed(0)}%
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 text-sm sm:text-base">Score: {selectedAttempt.score}/{selectedAttempt.max_score}</p>
                    <p className="text-xs sm:text-sm text-gray-500">
                      {formatDate(selectedAttempt.completed_at)} at {new Date(selectedAttempt.completed_at).toLocaleTimeString()}
                    </p>
                  </div>
                </div>
                <span className={`px-4 py-2 rounded-full text-sm font-medium ${
                  selectedAttempt.passed
                    ? 'bg-green-100 text-green-700'
                    : 'bg-red-100 text-red-700'
                }`}>
                  {selectedAttempt.passed ? '✓ Passed' : '✗ Failed'}
                </span>
              </div>

              {/* Questions List */}
              <div className="space-y-3 sm:space-y-4">
                {selectedAttempt.questions.map((question, qIndex) => (
                  <div
                    key={question.question_id}
                    className={`border rounded-lg overflow-hidden ${
                      question.is_correct
                        ? 'border-green-200 bg-green-50/30'
                        : 'border-red-200 bg-red-50/30'
                    }`}
                  >
                    {/* Question Header */}
                    <div className={`px-3 sm:px-4 py-2 sm:py-3 ${
                      question.is_correct ? 'bg-green-100' : 'bg-red-100'
                    }`}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-start gap-2 sm:gap-3 flex-1 min-w-0">
                          <span className={`flex-shrink-0 w-5 h-5 sm:w-6 sm:h-6 rounded-full flex items-center justify-center text-xs font-medium ${
                            question.is_correct
                              ? 'bg-green-600 text-white'
                              : 'bg-red-600 text-white'
                          }`}>
                            {qIndex + 1}
                          </span>
                          <div className="min-w-0 flex-1">
                            <p className="text-xs sm:text-sm font-medium text-gray-900 break-words">{question.question_text}</p>
                            <p className="text-xs text-gray-500 mt-1">
                              {question.question_type === 'SINGLE_CHOICE' && 'Single Choice'}
                              {question.question_type === 'MULTIPLE_CHOICE' && 'Multiple Choice'}
                              {question.question_type === 'TEXT_ANSWER' && 'Text Answer'}
                              {question.question_type === 'TRUE_FALSE' && 'True / False'}
                              {' • '}{question.points_earned}/{question.points_possible} pts
                            </p>
                          </div>
                        </div>
                        {question.is_correct ? (
                          <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-600 flex-shrink-0" />
                        ) : (
                          <XCircle className="w-4 h-4 sm:w-5 sm:h-5 text-red-600 flex-shrink-0" />
                        )}
                      </div>
                    </div>

                    {/* Question Body */}
                    <div className="p-3 sm:p-4">
                      {/* For Choice Questions */}
                      {(question.question_type === 'SINGLE_CHOICE' || question.question_type === 'MULTIPLE_CHOICE') && question.options && (
                        <div className="space-y-2">
                          {question.options.map((option) => {
                            const isUserSelected = question.user_selected_option_ids?.includes(option.id);
                            const isCorrect = question.correct_option_ids?.includes(option.id);

                            let optionClass = 'bg-gray-50 border-gray-200';
                            let iconElement = null;

                            if (isCorrect && isUserSelected) {
                              optionClass = 'bg-green-100 border-green-400';
                              iconElement = <CheckCircle className="w-4 h-4 text-green-600" />;
                            } else if (isCorrect && !isUserSelected) {
                              optionClass = 'bg-green-50 border-green-300 border-dashed';
                              iconElement = <CheckCircle className="w-4 h-4 text-green-500" />;
                            } else if (!isCorrect && isUserSelected) {
                              optionClass = 'bg-red-100 border-red-400';
                              iconElement = <XCircle className="w-4 h-4 text-red-600" />;
                            }

                            return (
                              <div
                                key={option.id}
                                className={`flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-3 p-2 sm:p-3 rounded-lg border ${optionClass}`}
                              >
                                <div className="flex items-start gap-2 sm:gap-3 flex-1 min-w-0">
                                  <div className="flex-shrink-0 mt-0.5">
                                    {iconElement || (
                                      <div className="w-4 h-4 rounded-full border-2 border-gray-300" />
                                    )}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-xs sm:text-sm text-gray-900 break-words">{option.text}</p>
                                    {(isUserSelected || isCorrect) && option.explanation && (
                                      <p className="text-xs text-gray-500 mt-1 italic">{option.explanation}</p>
                                    )}
                                  </div>
                                </div>
                                {(isUserSelected || isCorrect) && (
                                  <div className="flex-shrink-0 flex flex-wrap gap-1 ml-6 sm:ml-0">
                                    {isUserSelected && (
                                      <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded whitespace-nowrap">Your Answer</span>
                                    )}
                                    {isCorrect && (
                                      <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded whitespace-nowrap">Correct</span>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {/* For True/False Questions */}
                      {question.question_type === 'TRUE_FALSE' && (
                        <div className="space-y-2">
                          {question.options && question.options.length > 0 ? (
                            question.options.map((option) => {
                              const isUserSelected = question.user_selected_option_ids?.includes(option.id);
                              const isCorrect = question.correct_option_ids?.includes(option.id) || option.is_correct;

                              let optionClass = 'bg-gray-50 border-gray-200';
                              let iconElement = null;

                              if (isCorrect && isUserSelected) {
                                optionClass = 'bg-green-100 border-green-400';
                                iconElement = <CheckCircle className="w-4 h-4 text-green-600" />;
                              } else if (isCorrect && !isUserSelected) {
                                optionClass = 'bg-green-50 border-green-300 border-dashed';
                                iconElement = <CheckCircle className="w-4 h-4 text-green-500" />;
                              } else if (!isCorrect && isUserSelected) {
                                optionClass = 'bg-red-100 border-red-400';
                                iconElement = <XCircle className="w-4 h-4 text-red-600" />;
                              }

                              return (
                                <div
                                  key={option.id}
                                  className={`flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 p-2 sm:p-3 rounded-lg border ${optionClass}`}
                                >
                                  <div className="flex items-center gap-2 sm:gap-3 flex-1">
                                    <div className="flex-shrink-0">
                                      {iconElement || (
                                        <div className="w-4 h-4 rounded-full border-2 border-gray-300" />
                                      )}
                                    </div>
                                    <p className="text-xs sm:text-sm text-gray-900">{option.text}</p>
                                  </div>
                                  {(isUserSelected || isCorrect) && (
                                    <div className="flex-shrink-0 flex flex-wrap gap-1 ml-6 sm:ml-0">
                                      {isUserSelected && (
                                        <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded whitespace-nowrap">Your Answer</span>
                                      )}
                                      {isCorrect && (
                                        <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded whitespace-nowrap">Correct</span>
                                      )}
                                    </div>
                                  )}
                                </div>
                              );
                            })
                          ) : (
                            ['True', 'False'].map((value) => {
                              const valueLower = value.toLowerCase();
                              const isUserSelected = question.user_selected_option_ids?.includes(valueLower) || question.user_text_answer?.toLowerCase() === valueLower;
                              const isCorrect = question.correct_text_answer?.toLowerCase() === valueLower;

                              let optionClass = 'bg-gray-50 border-gray-200';
                              let iconElement = null;

                              if (isCorrect && isUserSelected) {
                                optionClass = 'bg-green-100 border-green-400';
                                iconElement = <CheckCircle className="w-4 h-4 text-green-600" />;
                              } else if (isCorrect && !isUserSelected) {
                                optionClass = 'bg-green-50 border-green-300 border-dashed';
                                iconElement = <CheckCircle className="w-4 h-4 text-green-500" />;
                              } else if (!isCorrect && isUserSelected) {
                                optionClass = 'bg-red-100 border-red-400';
                                iconElement = <XCircle className="w-4 h-4 text-red-600" />;
                              }

                              return (
                                <div
                                  key={value}
                                  className={`flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 p-2 sm:p-3 rounded-lg border ${optionClass}`}
                                >
                                  <div className="flex items-center gap-2 sm:gap-3 flex-1">
                                    <div className="flex-shrink-0">
                                      {iconElement || (
                                        <div className="w-4 h-4 rounded-full border-2 border-gray-300" />
                                      )}
                                    </div>
                                    <p className="text-xs sm:text-sm text-gray-900">{value}</p>
                                  </div>
                                  {(isUserSelected || isCorrect) && (
                                    <div className="flex-shrink-0 flex flex-wrap gap-1 ml-6 sm:ml-0">
                                      {isUserSelected && (
                                        <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded whitespace-nowrap">Your Answer</span>
                                      )}
                                      {isCorrect && (
                                        <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded whitespace-nowrap">Correct</span>
                                      )}
                                    </div>
                                  )}
                                </div>
                              );
                            })
                          )}
                        </div>
                      )}

                      {/* For Text Answer Questions */}
                      {question.question_type === 'TEXT_ANSWER' && (
                        <div className="space-y-2 sm:space-y-3">
                          <div className="p-2 sm:p-3 bg-blue-50 border border-blue-200 rounded-lg">
                            <p className="text-xs text-blue-600 font-medium mb-1">Your Answer:</p>
                            <p className="text-xs sm:text-sm text-gray-900 break-words">
                              {question.user_text_answer || <span className="text-gray-400 italic">No answer provided</span>}
                            </p>
                          </div>
                          {question.correct_text_answer && (
                            <div className="p-2 sm:p-3 bg-green-50 border border-green-200 rounded-lg">
                              <p className="text-xs text-green-600 font-medium mb-1">Expected Answer:</p>
                              <p className="text-xs sm:text-sm text-gray-900 break-words">{question.correct_text_answer}</p>
                            </div>
                          )}
                          {question.answer_keywords && question.answer_keywords.length > 0 && (
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-xs text-gray-500">Keywords:</span>
                              {question.answer_keywords.map((keyword, kIdx) => (
                                <span key={kIdx} className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded">
                                  {keyword}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Explanation */}
                      {question.explanation && (
                        <div className="mt-2 sm:mt-3 p-2 sm:p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                          <p className="text-xs text-yellow-700 font-medium mb-1">Explanation:</p>
                          <p className="text-xs sm:text-sm text-gray-700 break-words">{question.explanation}</p>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Modal>
      )}

      {/* Edit Tracking Settings Modal */}
      <Modal
        isOpen={showEditModal}
        onClose={() => !editLoading && setShowEditModal(false)}
        title="Edit Tracking Settings"
        size="md"
        footer={
          <div className="flex items-center justify-end gap-3">
            <button
              onClick={() => setShowEditModal(false)}
              disabled={editLoading}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveSettings}
              disabled={editLoading}
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
            >
              {editLoading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Save Changes
                </>
              )}
            </button>
          </div>
        }
      >
        <div className="space-y-5">
          {/* Mandatory */}
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-900">Mandatory</p>
              <p className="text-xs text-gray-500">Mark this assignment as mandatory</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={editForm.is_mandatory}
                onChange={(e) => setEditForm(prev => ({ ...prev, is_mandatory: e.target.checked }))}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600" />
            </label>
          </div>

          {/* Deadline Date */}
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-1">Deadline Date</label>
            <p className="text-xs text-gray-500 mb-2">Must be today or a future date</p>
            <input
              type="date"
              value={editForm.deadline_date}
              min={new Date().toISOString().split('T')[0]}
              onChange={(e) => setEditForm(prev => ({ ...prev, deadline_date: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>

          {/* Send Notification */}
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-900">Send Notification</p>
              <p className="text-xs text-gray-500">Turning off also disables notifications</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={editForm.send_notification}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setEditForm(prev => ({
                    ...prev,
                    send_notification: checked,
                    ...(checked ? {} : { enable_automatic_reminder: false, reminder_frequency_days: '' as any }),
                  }));
                }}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600" />
            </label>
          </div>

          {/* Enable Automatic Reminder */}
          <div className={`flex items-center justify-between ${!editForm.send_notification ? 'opacity-50' : ''}`}>
            <div>
              <p className="text-sm font-medium text-gray-900">Automatic Reminder</p>
              <p className="text-xs text-gray-500">Must be enabled to receive the deadline reminder (sent 1 day before the due date) and periodic assignment reminders at the set frequency.</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={editForm.enable_automatic_reminder}
                disabled={!editForm.send_notification}
                onChange={(e) => setEditForm(prev => ({ ...prev, enable_automatic_reminder: e.target.checked, ...(!e.target.checked ? { reminder_frequency_days: '' as any } : {}) }))}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600 disabled:cursor-not-allowed" />
            </label>
          </div>

          {/* Reminder Frequency */}
          <div className={`${!editForm.enable_automatic_reminder || !editForm.send_notification ? 'opacity-50' : ''}`}>
            <label className="block text-sm font-medium text-gray-900 mb-1">Reminder Frequency (days)</label>
            <p className="text-xs text-gray-500 mb-2">Must be greater than 0</p>
            <input
              type="number"
              min={1}
              max={100}
              placeholder="Enter days (1-100)"
              value={editForm.reminder_frequency_days}
              disabled={!editForm.enable_automatic_reminder || !editForm.send_notification}
              onChange={(e) => setEditForm(prev => ({ ...prev, reminder_frequency_days: e.target.value === '' ? '' as any : parseInt(e.target.value) || '' }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
            />
          </div>
        </div>
      </Modal>

      {/* Full Title Modal */}
      <Modal
        isOpen={showTitleModal}
        onClose={() => setShowTitleModal(false)}
        title="Course Title"
        size="md"
      >
        <div className="p-2">
          <p className="text-sm sm:text-base text-gray-800 break-words whitespace-pre-wrap">
            {courseInfo?.title}
          </p>
        </div>
      </Modal>

      {/* Revoke Course Confirmation Modal */}
      <Modal
        isOpen={showRevokeModal}
        onClose={() => !revokeLoading && setShowRevokeModal(false)}
        title="Revoke Course Access"
        size="sm"
        closeOnOverlayClick={!revokeLoading}
        footer={
          <div className="flex flex-col-reverse sm:flex-row items-center justify-end gap-2 sm:gap-3">
            <button
              onClick={() => setShowRevokeModal(false)}
              disabled={revokeLoading}
              className="w-full sm:w-auto px-4 sm:px-6 py-2.5 border-2 border-gray-300 text-gray-700 bg-white rounded-lg hover:bg-gray-50 transition-colors font-medium disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleRevokeCourse}
              disabled={revokeLoading}
              className="w-full sm:w-auto px-4 sm:px-6 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-semibold disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {revokeLoading ? (
                <>
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Revoking...
                </>
              ) : (
                <>
                  <XCircle className="w-4 h-4" />
                  Yes, Revoke Course
                </>
              )}
            </button>
          </div>
        }
      >
        <div className="flex flex-col items-center sm:items-start sm:flex-row gap-3 sm:gap-4">
          <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0">
            <AlertCircle className="w-6 h-6 text-red-600" />
          </div>
          <div className="text-center sm:text-left">
            <p className="text-sm text-gray-600 leading-relaxed">
              Are you sure you want to revoke this course for <span className="font-semibold text-gray-900">{userInfo?.name || 'this user'}</span>?
            </p>
            <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-xs sm:text-sm text-red-700 font-medium mb-1">This will permanently delete:</p>
              <ul className="text-xs sm:text-sm text-red-600 list-disc list-inside space-y-0.5">
                <li>Course tracking & progress</li>
                <li>All assessment attempts</li>
                <li>Issued certificates</li>
              </ul>
            </div>
            <p className="mt-2 text-xs text-gray-500">This action cannot be undone.</p>
          </div>
        </div>
      </Modal>
    </div>
  );
}